import type { ReactNode } from 'react'
import { Button, Group, Text } from '@grovus/design-system'
import type { AccountId } from '../data/account'
import type { Client } from '../data/clients'
import { fmt } from '../lib/format'
import type { Dict, Lang } from '../i18n'
import type { Bounds } from '../creatives/contract'
import type { Period, PresetDays } from '../lib/period'
import { AccountPanel } from './AccountPanel'
import type { AccountConnections } from './useAccountConnections'
import { ClientPanel, clientLabel } from './ClientPanel'
import type { CompanyCreation } from './useCompanyCreation'
import { HelpPanel } from './HelpPanel'
import { PeriodControl } from './PeriodControl'
import { AccountTicks } from './Ticks'
import { UserMenu, type UserMenuAccount } from './UserMenu'
import { Wordmark } from './Wordmark'
import type { PanelProps, TriggerProps } from './useDisclosurePanel'

interface Props {
  t: Dict
  /** True once the active client has no ad account connected. It hides the period control
   * alone: that rule is GRO-19's and this task does not revisit it. The accounts panel is
   * scoped to the client now, so it is shown rather than hidden - its Meta section reads
   * "Not connected yet", which is accurate where the fixture's three accounts were not. */
  emptyCompany?: boolean
  /** The active client. Absent only where the header renders without a shell around it. */
  client?: Client
  /** Every client the user works with, for the switch. */
  clients?: Client[]
  /** The signed-in account for the user menu and the avatar (GRO-106). */
  account?: UserMenuAccount
  /** Platform approval: "Add client" in the switcher shows only with it. */
  canCreate?: boolean
  onRequestPermission?: () => void
  onSelectClient?: (id: string) => void
  clientPanel?: boolean
  clientTriggerProps?: TriggerProps
  clientPanelProps?: PanelProps
  /** The add-client attempts and their handlers (GRO-82), owned by App. */
  creation?: CompanyCreation
  /** Close the open panel and return focus to its trigger. Selection uses it. */
  closePanel?: () => void
  accs?: Record<AccountId, boolean>
  toggleAcc?: (id: AccountId) => void
  accPanel?: boolean
  accountTriggerProps?: TriggerProps
  accountPanelProps?: PanelProps
  /** The account panel's connection surface (GRO-41). */
  connections?: AccountConnections
  lang?: Lang
  period?: Period
  onChangePeriod?: (period: Period) => void
  /** The presets on offer; the old contract's three while the shell talks to an old server. */
  presets?: readonly PresetDays[]
  /** The range picker's limits for the current selection, or `null` while unknown. */
  bounds?: Bounds | null
  narrow?: boolean
  periodPanel?: boolean
  periodTriggerProps?: TriggerProps
  periodPanelProps?: PanelProps
  /** The session settings: language, palette and sign-out. They live in the user menu at
   * every width and never in the header row (GRO-66). */
  settings?: ReactNode
  helpPanel?: boolean
  helpTriggerProps?: TriggerProps
  helpPanelProps?: PanelProps
  userPanel?: boolean
  userTriggerProps?: TriggerProps
  userPanelProps?: PanelProps
  /** While sign-out is pending the avatar says so and is marked busy. */
  signOutPending?: boolean
}

/** A person glyph for the user menu's trigger while the email is unknown. */
function PersonGlyph() {
  return (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
    </svg>
  )
}

/**
 * The header row's content, not the `<header>` landmark itself: AppShell owns
 * that element, so this positions its account dropdown against `top: 100%` of
 * whatever the header's actual rendered height turns out to be, rather than a
 * fixed offset that a wrapped header would make wrong.
 */
export function Header({
  t, emptyCompany, client, clients, account, canCreate = true, onRequestPermission,
  onSelectClient, clientPanel, clientTriggerProps, clientPanelProps, closePanel, creation,
  accs = {} as Record<AccountId, boolean>,
  toggleAcc, accPanel, accountTriggerProps, accountPanelProps, connections,
  lang = 'en', period, onChangePeriod, presets = [], bounds = null, narrow = false,
  periodPanel, periodTriggerProps, periodPanelProps, settings,
  helpPanel, helpTriggerProps, helpPanelProps, userPanel, userTriggerProps, userPanelProps,
  signOutPending = false,
}: Props) {
  const showClient = client !== undefined && clients !== undefined && clientTriggerProps !== undefined
  const owned = client?.accounts ?? []
  const active = owned.filter((a) => accs[a.id] !== false)
  // The merged phrasing applies only when the client owns at least one account and every
  // one of them is checked. Every other case, zero of zero included, counts them out: a
  // client with nothing connected has merged nothing.
  const summary = owned.length > 0 && active.length === owned.length
    ? fmt(t.accMerged, { n: owned.length })
    : fmt(t.accOf, { n: active.length, t: owned.length })

  return (
    <>
      <Group align="flex-end" gap={10} mr={showClient ? undefined : 'auto'}>
        <Wordmark />
        <Text fz={11} tt="uppercase" className="app-shell-tagline grv-muted" style={{ letterSpacing: '0.12em' }}>Creative intelligence</Text>
      </Group>

      {showClient && (
        <>
          <Button variant="default" mr="auto" className="app-shell-client-pill" {...clientTriggerProps} data-testid="client-panel-toggle">
            {clientLabel(client, t)}
            {/* The text presentation selector keeps the caret a glyph in the ink colour. It
                is decoration: `aria-expanded` already says the button opens something. */}
            <span aria-hidden="true" className="app-shell-client-caret">{'▾︎'}</span>
          </Button>
          {clientPanel && clientPanelProps && (
            <ClientPanel
              t={t}
              clients={clients}
              activeId={client.id}
              onSelect={(id) => onSelectClient?.(id)}
              onClose={() => closePanel?.()}
              panelProps={clientPanelProps}
              creation={creation}
              lang={lang}
              canCreate={canCreate}
              onRequestPermission={onRequestPermission}
              invitationCount={account?.invitationCount ?? 0}
              onOpenInvitations={account?.onOpenInvitations}
            />
          )}
        </>
      )}

      {client && accountTriggerProps && (
        <>
          <Button
            variant="default"
            {...accountTriggerProps}
            leftSection={<AccountTicks count={active.length} total={owned.length} />}
            data-testid="account-panel-toggle"
          >
            {summary}
          </Button>
          {/* Immediately after its own trigger, so Tab reaches the panel's contents next
              and then continues into the rest of the header. */}
          {accPanel && accountPanelProps && (
            <AccountPanel
              t={t}
              client={client}
              accs={accs}
              toggleAcc={(id) => toggleAcc?.(id)}
              panelProps={accountPanelProps}
              connections={connections}
              lang={lang}
            />
          )}
        </>
      )}

      {!emptyCompany && period !== undefined && onChangePeriod && (
        <PeriodControl
          t={t}
          lang={lang}
          period={period}
          onChange={onChangePeriod}
          presets={presets}
          bounds={bounds}
          narrow={narrow}
          rangeOpen={periodPanel}
          rangeTriggerProps={periodTriggerProps}
          rangePanelProps={periodPanelProps}
          closeRange={closePanel}
        />
      )}

      {helpTriggerProps && (
        <>
          <Button
            variant="default"
            className="header-icon-button"
            {...helpTriggerProps}
            aria-label={t.helpLabel}
            title={t.helpLabel}
            data-testid="help-toggle"
          >
            <span aria-hidden="true">?</span>
          </Button>
          {helpPanel && helpPanelProps && <HelpPanel t={t} panelProps={helpPanelProps} />}
        </>
      )}

      {userTriggerProps && (
        <>
          {/* The email's initial (GRO-106, Q2), and a brand dot while invitations wait. */}
          <span className="header-avatar-wrap">
            <Button
              variant="default"
              className="header-icon-button header-avatar"
              {...userTriggerProps}
              aria-label={signOutPending ? t.signingOut : t.userMenu}
              aria-busy={signOutPending || undefined}
              title={signOutPending ? t.signingOut : t.userMenu}
              data-testid="user-menu-toggle"
            >
              {account?.email ? <span aria-hidden="true">{account.email.charAt(0).toUpperCase()}</span> : <PersonGlyph />}
            </Button>
            {(account?.invitationCount ?? 0) > 0 && <span className="header-avatar-dot" aria-hidden="true" data-testid="avatar-dot" />}
          </span>
          {userPanel && userPanelProps && (
            <UserMenu t={t} lang={lang} account={account} settings={settings} panelProps={userPanelProps} />
          )}
        </>
      )}
    </>
  )
}
